import { EPaymentPlan } from '@if/core/types'
import { ControlledNumberField, ControlledSelect, ControlledStringDateOnlyPicker } from '@if/mui'
import { Grid, MenuItem } from '@mui/material'
import { programConfigByFinancingProgramId } from '@src/data/creditapp-selectors'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { getFirstPaymentOnMinMax, ProductsWorksheet } from '@src/types/WorksheetSchema'
import { JSX, useMemo } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApplication: CreditApplication
  control: Control<ProductsWorksheet>
  disabledAmountInput?: boolean
  maxTermDuration?: number
  loanTermChoicesList: JSX.Element[]
}

const ProductsWorksheetDetails = ({ creditApplication, control, disabledAmountInput, loanTermChoicesList }: Props) => {
  const { t } = useTranslation()
  const [paymentFrequency, deliveryOn] = useWatch({ control, name: ['paymentFrequency', 'deliveryOn'] })
  const maxActivationDate = useMemo(() => new Date(creditApplication.expiresOn), [creditApplication.expiresOn])

  const [minFirstPaymentDate, maxFirstPaymentDate] = useMemo(() => {
    return getFirstPaymentOnMinMax(deliveryOn, paymentFrequency)
  }, [deliveryOn, paymentFrequency])

  const programConfig = programConfigByFinancingProgramId(creditApplication.financingProgramId)
  const availableFrequencyPayments = programConfig.frequencyPayments

  const paymentFrequencyOptions = useMemo(
    () =>
      availableFrequencyPayments.map((value) => (
        <MenuItem key={value} value={value}>
          {t(`EPaymentFrequency.${value}`, { ns: 'enum' })}
        </MenuItem>
      )),
    [t, availableFrequencyPayments],
  )

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledNumberField
          control={control}
          name={`amountRequested`}
          label={t('worksheet.requestedAmount')}
          disabled={disabledAmountInput}
          fullWidth
          isMoney
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledStringDateOnlyPicker
          name="deliveryOn"
          control={control}
          label={t('worksheet.activationDate')}
          disablePast
          minDate={new Date()}
          maxDate={maxActivationDate}
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          name="term"
          control={control}
          options={loanTermChoicesList}
          label={t('worksheet.loanTerm')}
          disableEmptyOption
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          name="paymentFrequency"
          control={control}
          options={paymentFrequencyOptions}
          label={t('worksheet.paymentFrequency')}
          disableEmptyOption
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledStringDateOnlyPicker
          name="firstPaymentOn"
          control={control}
          label={t('worksheet.firstPaymentOn')}
          disablePast
          minDate={minFirstPaymentDate}
          maxDate={maxFirstPaymentDate}
          disabled={!paymentFrequency}
          fullWidth
        />
      </Grid>
      <Grid
        size={{
          xs: 6,
          md: 4,
        }}
      >
        <ControlledSelect
          name="paymentPlanId"
          control={control}
          label={t('worksheet.paymentPlan')}
          disableEmptyOption
          disabled
        >
          <MenuItem value={EPaymentPlan.RegularDailyInterests}>
            {t(`EPaymentPlan.${EPaymentPlan.RegularDailyInterests}`, { ns: 'enum' })}
          </MenuItem>
        </ControlledSelect>
      </Grid>
    </Grid>
  )
}

export default ProductsWorksheetDetails
